import React, { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useConfetti } from 'V2/Shared/hooks/useConfetti';
import { Accordian } from 'V2/Shared/Accordian';
import { ChevronDown, ChevronUp } from 'V2/Shared/Icons';
import { Spinner } from 'V2/Shared/Spinner';
import { CHECKOUT_SESSION_KEYS } from 'V2/Checkout/constants';
import { CheckoutPageLayout } from 'V2/Checkout/components';
import { ErrorRetry } from 'V2/Shared/ErrorRetry';
import { httpCheckoutAPI } from 'V2/Services/httpCheckoutAPI';
import styles from './styles.module.css';

import { BindSummary } from './types';
import {
  BindSummaryResponse,
  transformSummary,
} from './bindSummary.transformer';

type ConfirmationStatus = 'loading' | 'success' | 'error';

export const CheckoutConfirmation: React.FC = () => {
  const history = useHistory();
  const appId = sessionStorage.getItem(CHECKOUT_SESSION_KEYS.appId) ?? '';

  const [status, setStatus] = useState<ConfirmationStatus>('loading');
  const [summary, setSummary] = useState<BindSummary | null>(null);

  useEffect(() => {
    if (!appId) {
      history.replace('/search');
    }
  }, [appId, history]);

  const fetchSummary = useCallback(async () => {
    if (!appId) return;
    setStatus('loading');
    try {
      const response = await httpCheckoutAPI.get({
        url: `/bindapi/insurance_applications/${appId}/bind_summary`,
      });
      setSummary(transformSummary(response.data as BindSummaryResponse));
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }, [appId]);

  useEffect(() => {
    void fetchSummary();
  }, [fetchSummary]);

  const { confetti } = useConfetti({
    delay: 500,
    enabled: status === 'success',
  });

  const paymentMethodLabel = summary
    ? [summary.paymentMethod, summary.lastFourDigits]
        .filter(Boolean)
        .join(' - ')
    : '';

  if (status === 'loading') {
    return (
      <CheckoutPageLayout>
        <div className={styles.loaderWrapper} aria-label="Loading">
          <Spinner variant="blue" />
        </div>
      </CheckoutPageLayout>
    );
  }

  if (status === 'error') {
    return (
      <CheckoutPageLayout>
        <ErrorRetry onRetry={fetchSummary}>
          Unable to load your confirmation. Please refresh the page.
        </ErrorRetry>
      </CheckoutPageLayout>
    );
  }

  return (
    <CheckoutPageLayout>
      {confetti}

      <div className={styles.confirmationSection}>
        <h1 className={styles.title}>Your order has been confirmed!</h1>
        {summary?.email && (
          <p className={styles.emailNote}>
            We have sent you a receipt of your order to{' '}
            <strong>{summary.email}</strong>.
          </p>
        )}
        {!summary?.email && (
          <p className={styles.emailNote}>
            We have sent you a receipt of your order to your email address.
          </p>
        )}
      </div>

      <div className={styles.paymentDetails}>
        <h2 className={styles.sectionTitle}>Payment details:</h2>
        {summary?.downPaymentAmount && (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Amount</span>
            <span className={styles.detailValue}>
              {summary.downPaymentAmount}
            </span>
          </div>
        )}
        {!summary?.downPaymentAmount && summary?.totalPremium && (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Amount</span>
            <span className={styles.detailValue}>{summary.totalPremium}</span>
          </div>
        )}
        {paymentMethodLabel && (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Method</span>
            <span className={styles.detailValue}>{paymentMethodLabel}</span>
          </div>
        )}
      </div>

      <Accordian
        title="Order summary"
        initOpen
        className={styles.accordionCard}
        triggers={{
          expand: <ChevronDown color="#21262a" />,
          collapse: <ChevronUp color="#21262a" />,
        }}
      >
        <div className={styles.accordionInner}>
          {summary?.totalPremium && (
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Total premium</span>
              <span className={styles.summaryValue}>
                {summary.totalPremium}
              </span>
            </div>
          )}
          {summary?.effectiveDate && (
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Effective date</span>
              <span className={styles.summaryValue}>
                {summary.effectiveDate}
              </span>
            </div>
          )}
          {summary?.policyNumber && (
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Policy number</span>
              <span className={styles.summaryValue}>
                {summary.policyNumber}
              </span>
            </div>
          )}
          {summary?.installmentAmount && summary?.paymentFrequencyLabel && (
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>
                {summary.paymentFrequencyLabel} payments
              </span>
              <span className={styles.summaryValue}>
                {summary.installmentAmount}
              </span>
            </div>
          )}
        </div>
      </Accordian>
    </CheckoutPageLayout>
  );
};
